<template>
  <v-app>
    <router-view />

    <LoginPopup
      v-if="route.path !== '/reset-password' && route.path !== '/after-auth-google'"
      :visible="authState.showLoginPopup.value"
      @update:visible="updateVisible"
      @logged-in="handleLoggedIn"
    />
  </v-app>
</template>

<script lang="ts" setup>
  import { authState } from '@/interfaces/composables/auth/authState';
  import LoginPopup from '@/interfaces/components/auth/LoginPopup.vue';
  import { useRoute } from 'vue-router';
  import { usePasswordReset } from '@/interfaces/composables/auth/usePasswordReset';
  import { onMounted, watch } from 'vue';
  import { useSharedChatDetection } from '@/interfaces/composables/useSharedChatDetection';

  useSharedChatDetection();

  const route = useRoute();
  const { verifyToken } = usePasswordReset();

  function handleLoggedIn(username: string) {
    authState.userName.value = username;
    authState.isAuthenticated.value = true;
    authState.mode.value = 'login';
    authState.showLoginPopup.value = false;
  }

  // Typed handler for the LoginPopup's update:visible event to avoid implicit 'any' (TS7006)
  function updateVisible(v: boolean) {
    authState.showLoginPopup.value = v;
  }

  onMounted(async () => {
    const token = route.query.token as string | undefined;

    if (route.path === '/reset-password' && token) {
      const result = await verifyToken(token);

      if (result?.is_valid) {
        authState.resetPasswordToken.value = token;
        authState.mode.value = 'resetPassword';
        authState.showLoginPopup.value = true;
      } else {
        // Invalid or expired token
        authState.resetPasswordToken.value = null;
        authState.mode.value = 'login';
        authState.showLoginPopup.value = true;
      }
    } else {
      // ✅ Only fallback to login if auth has been checked AND user is not authenticated
      watch(
        () => ({
          isAuthenticated: authState.isAuthenticated.value,
          hasCheckedAuth: authState.hasCheckedAuth.value,
          showLoginPopup: authState.showLoginPopup.value,
        }),
        ({ isAuthenticated, hasCheckedAuth, showLoginPopup }) => {
          if (hasCheckedAuth && !isAuthenticated && !showLoginPopup) {
            authState.showLoginPopup.value = true;
          }
        },
        { immediate: true }
      );
    }
  });
</script>

<style>
  html,
  body,
  #app {
    height: 100%;
    margin: 0;
  }
</style>